import React, { useState, useRef, useEffect } from 'react';
import axiosInstance from '../utils/axiosInstance';
import config from '../utils/config';
import Plot from './Plot';
import { Note } from '../types';
import { buildPlotlyNoteOverlay, noteIdFromPlotlyClick } from '../utils/plotlyNoteOverlay';
import { toPlotlyLocal, axisTimeLabel } from '../utils/plotlyLocalTime';
import { unanalysedDaysOverlay } from '../utils/unanalysedDays';
import ChartCsvButton from './ChartCsvButton';
import { CsvColumn } from '../utils/chartCsv';
import { RAW_CHART_TOP_MARGIN, rawChartTitle } from '../utils/plotlyTitle';
import { HUMIDITY_ALERT_HIGH_PCT, HUMIDITY_ALERT_LOW_PCT } from '../utils/alertBounds';
import Button from './ui/Button';

interface TemperaturePlotReading {
  time: string;
  temperature?: number;
  humidity?: number;
}

type Metric = 'temperature' | 'humidity';

/** One raw-readings chart per metric, all served by the temperature-plot route. */
const METRICS: Record<Metric, {
  label: string;
  axis: string;
  csvChart: string;
  value: (r: TemperaturePlotReading) => number;
  fill: string;
  edge: string;
  /** Alert bounds drawn across the chart. The alerts judge the day's average,
   *  so single readings past a line do not alert on their own. */
  bounds?: { y: number; label: string }[];
}> = {
  temperature: {
    label: 'Temperature',
    axis: 'Temperature (°F)',
    csvChart: 'raw-temperature',
    value: (r) => (r.temperature as number) * 9 / 5 + 32,
    fill: 'rgba(234, 88, 12, 0.6)',
    edge: 'rgba(234, 88, 12, 1)',
  },
  humidity: {
    label: 'Humidity',
    axis: 'Humidity (%)',
    csvChart: 'raw-humidity',
    value: (r) => r.humidity as number,
    fill: 'rgba(37, 99, 235, 0.6)',
    edge: 'rgba(37, 99, 235, 1)',
    bounds: [
      { y: HUMIDITY_ALERT_HIGH_PCT, label: `High alert ${HUMIDITY_ALERT_HIGH_PCT}%<br>(daily average)` },
      { y: HUMIDITY_ALERT_LOW_PCT, label: `Low alert ${HUMIDITY_ALERT_LOW_PCT}%<br>(daily average)` },
    ],
  },
};

interface TemperaturePlotData {
  device_name: string;
  start_date: string;
  end_date: string;
  // FS-622: IANA zone of the device's own location, resolved server-side from
  // the ZIP on file. Absent when the device has no ZIP or no cached
  // coordinates for it — then the axis says it is showing the viewer's time.
  time_zone?: string | null;
  readings: TemperaturePlotReading[];
}

interface TemperaturePlotChartProps {
  deviceId: string;
  startDate: string;
  endDate: string;
  notes?: Note[];
  noteColor?: string;
  onNoteImageClick?: (noteId: number) => void;
  /** The last day the daily analysis has processed (YYYY-MM-DD). When
   *  `endDate` runs past it (it runs on to today, as the scatter does), the
   *  later days are raw readings only — the caption names both ends and a band
   *  shades the unanalysed days. */
  analysedThrough?: string;
  metric?: Metric;
}

const TemperaturePlotChart: React.FC<TemperaturePlotChartProps> = ({
  deviceId,
  startDate,
  endDate,
  notes,
  noteColor = '#ea580c',
  onNoteImageClick,
  analysedThrough,
  metric = 'temperature',
}) => {
  const spec = METRICS[metric];
  const [data, setData] = useState<TemperaturePlotData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const chartRef = useRef<HTMLDivElement>(null);

  const fetchTemperaturePlotData = async () => {
    setLoading(true);
    setError(null);

    const start = new Date(startDate);
    const end = new Date(endDate);
    const daysDiff = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));

    if (daysDiff > 400) {
      setError('Date range too large (max 400 days). Please reduce the range.');
      setLoading(false);
      return;
    }

    try {
      const response = await axiosInstance.get<TemperaturePlotData>(config.temperaturePlotApiUrl!, {
        params: {
          device_id: deviceId,
          start_date: startDate,
          end_date: endDate,
          ...(metric === 'temperature' ? {} : { metric }),
        },
        timeout: 60000
      });

      setData(response.data);
      setIsVisible(true);
    } catch (err) {
      const axiosError = err as { code?: string; response?: { status?: number; data?: { error?: string } } };
      if (axiosError.code === 'ECONNABORTED') {
        setError('Request timed out. Try a smaller date range.');
      } else if (axiosError.response?.status === 503) {
        setError('Service timeout. The date range may be too large - try reducing it.');
      } else {
        setError(`Failed to load ${spec.label.toLowerCase()} plot data. ` + (axiosError.response?.data?.error || ''));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = () => {
    if (isVisible) {
      setIsVisible(false);
    } else if (!data) {
      fetchTemperaturePlotData();
    } else {
      setIsVisible(true);
    }
  };

  useEffect(() => {
    if (isVisible && chartRef.current) {
      setTimeout(() => {
        chartRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, [isVisible]);

  const deviceTimeZone = data?.time_zone || null;

  const noteOverlay = buildPlotlyNoteOverlay({
    notes,
    color: noteColor,
    rangeStart: startDate,
    rangeEnd: endDate,
    timeZone: deviceTimeZone,
  });

  // Prepare plotly data. The API returns null (not []) when the range has
  // no readings, so guard before mapping.
  const readings = data?.readings ?? [];
  const timeAxisLabel = axisTimeLabel(deviceTimeZone, readings.map((r) => r.time));
  const basePlotData = data ? [{
    x: readings.map(r => toPlotlyLocal(r.time, deviceTimeZone)),
    y: readings.map(spec.value),
    mode: 'markers' as const,
    type: 'scatter' as const,
    marker: {
      size: 4,
      color: spec.fill,
      line: {
        color: spec.edge,
        width: 0.5
      }
    },
    name: spec.label
  }] : [];
  const csvColumns: CsvColumn<(typeof readings)[number]>[] = [
    { header: 'Time (UTC)', value: 'time' },
    { header: timeAxisLabel, value: (r) => toPlotlyLocal(r.time, deviceTimeZone) },
    { header: spec.axis, value: spec.value },
  ];
  const plotData = noteOverlay.trace ? [...basePlotData, noteOverlay.trace] : basePlotData;

  // Days after the last processed one have readings but no daily analysis
  // yet — shaded, labelled and captioned the same way as the scatter.
  const unanalysed = unanalysedDaysOverlay(endDate, analysedThrough, deviceTimeZone);

  const bounds = spec.bounds ?? [];
  const boundShapes = bounds.map((b) => ({
    type: 'line' as const,
    xref: 'paper' as const,
    x0: 0,
    x1: 1,
    yref: 'y' as const,
    y0: b.y,
    y1: b.y,
    line: { color: 'orange', width: 1.5, dash: 'dash' as const },
  }));
  // Labels sit in the right margin, past the last reading.
  const boundLabels = bounds.map((b) => ({
    xref: 'paper' as const,
    x: 1,
    xanchor: 'left' as const,
    yref: 'y' as const,
    y: b.y,
    yanchor: 'middle' as const,
    text: b.label,
    showarrow: false,
    align: 'left' as const,
    font: { size: 11, color: '#6b7280' },
  }));

  const layout = {
    title: rawChartTitle(data ? `${spec.label} Plot - ${data.device_name}` : `${spec.label} Plot`),
    xaxis: {
      title: { text: timeAxisLabel },
      type: 'date' as const
    },
    yaxis: {
      title: { text: spec.axis }
    },
    yaxis2: {
      overlaying: 'y' as const,
      showgrid: false,
      showticklabels: false,
      zeroline: false,
      range: [0, 1],
      fixedrange: true,
    },
    shapes: [...unanalysed.shapes, ...boundShapes, ...noteOverlay.shapes],
    annotations: [...unanalysed.annotations, ...boundLabels],
    hovermode: 'closest' as const,
    autosize: true,
    margin: { t: RAW_CHART_TOP_MARGIN, r: bounds.length ? 120 : 50, b: 50, l: 60 },
    // Preserve user zoom/pan across re-renders (e.g. the AuthContext 60s
    // token check re-renders the tree with a fresh layout object).
    uirevision: 'keep'
  };

  const handleClick = (event: unknown) => {
    const noteId = noteIdFromPlotlyClick(event);
    if (noteId != null && onNoteImageClick) onNoteImageClick(noteId);
  };

  return (
    <div className="mt-4">
      <Button
        variant="link"
        size="md"
        onClick={handleToggle}
        disabled={loading}
        title={`Show or hide a plot of every raw ${spec.label.toLowerCase()} reading in this date range (they load the first time you open it)`}
      >
        {loading ? 'Loading...' : isVisible ? `Hide ${spec.label} Chart` : `View Raw ${spec.label} Chart`}
      </Button>

      {error && (
        <div className="mt-2 text-status-error-foreground text-sm">
          {error}
        </div>
      )}

      {isVisible && data && readings.length === 0 && (
        <div className="mt-2 text-sm text-muted-foreground">
          No readings for this device in the selected date range.
        </div>
      )}

      {isVisible && data && readings.length > 0 && (
        <div ref={chartRef} className="mt-4 border rounded-lg p-4 bg-white">
          <div className="flex justify-end">
            <ChartCsvButton
              chart={spec.csvChart}
              rows={readings}
              columns={csvColumns}
              startDate={data.start_date}
              endDate={data.end_date}
            />
          </div>
          <Plot
            data={plotData}
            layout={layout}
            config={{ responsive: true }}
            style={{ width: '100%', height: '500px' }}
            onClick={handleClick}
          />
          <div className="mt-2 text-xs text-muted-foreground">
            Date Range: {data.start_date} to {data.end_date} | Total Readings: {readings.length}
          </div>
          {unanalysed.caption && (
            <div className="mt-1 text-xs text-muted-foreground">{unanalysed.caption}</div>
          )}
        </div>
      )}
    </div>
  );
};

export default TemperaturePlotChart;
